import React from 'react';
import PropTypes from 'prop-types';

/**
 * ErrorDisplay component to show error messages
 */
const ErrorDisplay = ({ 
  error, 
  title = 'Error', 
  message = 'An unexpected error occurred', 
  onRetry,
  showDetails = false,
  className = ''
}) => {
  return (
    <div className={`rounded-md bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800 ${className}`} role="alert">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-500 dark:text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-red-800 dark:text-red-300">{title}</h3>
          <div className="mt-2 text-sm text-red-700 dark:text-red-400">
            <p>{message}</p>
            
            {showDetails && error && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer">Show technical details</summary>
                <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/40 overflow-auto rounded">
                  {typeof error === 'string' ? (
                    <p>{error}</p>
                  ) : (
                    <pre>{error.toString()}{error.stack ? `\n${error.stack}` : ''}</pre>
                  )}
                </div>
              </details>
            )}
          </div>
          
          {onRetry && (
            <div className="mt-4">
              <button
                type="button"
                className="rounded bg-red-50 dark:bg-red-900/30 px-2 py-1.5 text-sm font-medium text-red-800 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                onClick={onRetry}
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ErrorDisplay.propTypes = {
  /** Error object */
  error: PropTypes.any,
  /** Error title */
  title: PropTypes.string,
  /** Error message */
  message: PropTypes.string,
  /** Retry handler function */
  onRetry: PropTypes.func,
  /** Whether to show technical error details */
  showDetails: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string
};

export default ErrorDisplay; 